import { useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '@/store/hooks';
import { useGetPatientReportsQuery } from '@/store/slices/api/reportsApiSlice';
import { selectIsAnyFormDirty } from '@/store/slices/form/transmissionReportFormSlice';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { routes } from '@/routes';

import { LabelAndValue } from '@/types/custom';
import ConfirmationModal from '@/ui/ConfirmationModal';
import Dropdown from '@/ui/Dropdown';

import { formatTransmissionDate } from '../../ReportPatientDetails/utils';

export const TransmissionSelection = ({
  reportId,
  patientId
}: {
  reportId: number;
  patientId: string;
}) => {
  const { data, isLoading } = useGetPatientReportsQuery({ patientId });
  const [selectedReport, setSelectedReport] = useState<LabelAndValue | null>(null);
  const isAnyFormDirty = useAppSelector(selectIsAnyFormDirty);

  const { requestNavigation, confirmNavigation, cancelNavigation, isModalOpen } =
    useSafeNavigate(isAnyFormDirty);

  const options = useMemo((): LabelAndValue[] => {
    return (
      data?.transmissionReports.map((report) => ({
        value: report.id.toString(),
        label: `${report.deviceType} - ${formatTransmissionDate(report.transmissionAt)}`
      })) ?? []
    );
  }, [data]);

  const currentReportOption = useMemo(() => {
    return options.find((option) => option.value === reportId.toString()) || null;
  }, [options, reportId]);

  const handleChange = (value: LabelAndValue | LabelAndValue[] | null) => {
    if (!value || Array.isArray(value)) return;

    const selectedOption = value as LabelAndValue;
    setSelectedReport(selectedOption);
    requestNavigation(routes.physician.transmissionReportDetail(selectedOption.value));
  };

  useEffect(() => {
    setSelectedReport(currentReportOption);
  }, [currentReportOption]);

  return (
    <>
      <Dropdown
        options={options}
        onChange={handleChange}
        value={selectedReport as any}
        placeholder={isLoading ? 'Loading reports...' : 'Select a report'}
        isDisabled={isLoading}
      />
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={cancelNavigation}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to leave this page?"
        onConfirm={confirmNavigation}
        isLoading={false}
      />
    </>
  );
};
